import { cn } from '@dfh-corp-sites/ui/utils'
import { ChevronRight } from 'lucide-react'
import React, { useMemo, useState } from 'react'

import { MainMenuFragmentFragment } from '../../../graphql/gatsby'
import { useHeaderStyle } from '../../../lib/header/style'
import { useSanityRoleAndContextBasedContent } from '../../../lib/sanity'
import { filterMenuEntriesToDisplay } from '../../../utils/sanity/documents/menuEntry'
import { MenuEntryContent } from './menu-entry-content/MenuEntryContent'
import SubMenuEntry from './SubMenuEntry'

export interface NavDropdownProps {
  entry: NonNullable<MainMenuFragmentFragment['mainMenu']>[number]
  isOpen: boolean
  threshold: number
}

export const NavDropdown = React.forwardRef<HTMLDivElement, NavDropdownProps>(
  ({ entry, isOpen, threshold }, ref) => {
    const { shouldDisplayContent } = useSanityRoleAndContextBasedContent()
    const { headerStyle } = useHeaderStyle()

    const subMenuEntries = useMemo(
      () => filterMenuEntriesToDisplay(shouldDisplayContent)(entry?.childEntries ?? []),
      [entry?.childEntries, shouldDisplayContent],
    )

    const [hoveredChildEntry, setHoveredChildEntry] = useState<
      (typeof subMenuEntries)[number] | null
    >(null)

    const subMenuEntryElements = useMemo(
      () =>
        subMenuEntries.map((child) => {
          if (!child) return null
          return (
            <li
              key={child._id ?? child.name}
              className={cn(
                'flex items-center gap-1.5 font-normal',
                hoveredChildEntry === child && '!font-medium',
              )}
              onMouseEnter={() => setHoveredChildEntry(child)}
              onFocus={() => setHoveredChildEntry(child)}
            >
              <SubMenuEntry menuEntry={child} />
              {child.content?.show && (
                <ChevronRight
                  className={cn('size-8 flex-none', { invisible: hoveredChildEntry !== child })}
                />
              )}
            </li>
          )
        }),
      [hoveredChildEntry, subMenuEntries],
    )

    const contentToShow =
      hoveredChildEntry?.content && hoveredChildEntry.content.contentType
        ? hoveredChildEntry.content
        : entry?.content

    if (!isOpen || subMenuEntries.length === 0) {
      return null
    }

    return (
      <div
        ref={ref}
        style={{ top: threshold + 1 }}
        className={cn(
          'fixed inset-x-0',
          'z-50',
          'bg-black/40',
          'pointer-events-none',
          'h-screen',
          'text-brand-navy',
          'w-screen',
          headerStyle === 'transparent' && 'border-t border-brand-rule-grey',
        )}
        onMouseLeave={() => setHoveredChildEntry(null)}
      >
        <div className="shadow-2xl bg-white w-full pointer-events-auto">
          <div className="w-full max-w-[1440px] pt-5 lg:pt-10 pb-8 flex">
            <div className="w-auto flex border-r border-black md:ml-10 xl:ml-[215px]">
              <ul className="flex flex-col gap-2 max-h-[500px] overflow-auto p-2 mr-10 w-full text-xl lg:text-2xl leading-[1.6] flex-nowrap">
                {subMenuEntryElements}
              </ul>
            </div>

            <div className="flex-1 px-10 lg:px-16 overflow-auto">
              <MenuEntryContent content={contentToShow} />
            </div>
          </div>
        </div>
      </div>
    )
  },
)

NavDropdown.displayName = 'NavDropdown'
